import { EntityClass } from '@moderepo/mode-internal-apis';
/**
 * Helper function to create the keyPrefix for an entity class
 */
export const getEntityClassKeyPrefix = (entityClass: string): string => {
    return `entity_class.${entityClass}`;
};

/**
 * Helper function to create translation key for entity class display name
 */
export const getEntityClassNameKey = (keyPrefix: string) => {
    return `${keyPrefix}.display_settings.name`;
};

/**
 * Helper function to create translation key for entity class description
 */
export const getEntityClassDescriptionKey = (keyPrefix: string) => {
    return `${keyPrefix}.display_settings.description`;
};

/**
 * Helper function to create the keyPrefix for an attribute schema
 */
export const getAttributeSchemaKeyPrefix = (keyPrefix: string, attributeSchemaKey: string): string => {
    return `${keyPrefix}.attribute_schema.${attributeSchemaKey}`;
};

/**
 * Helper function to create translation key for an attribute schema label
 */
export const getAttributeSchemaLabelKey = (keyPrefix: string): string => {
    return `${keyPrefix}.label`;
};

/**
 * Helper function to create translation key for an attribute description label
 */
export const getAttributeSchemaDescriptionKey = (keyPrefix: string): string => {
    return `${keyPrefix}.description`;
};

/**
 * Helper function to create the keyPrefix for an attribute schema option
 */
export const getAttributeSchemaOptionKeyPrefix = (keyPrefix: string, optionValue: string): string => {
    return `${keyPrefix}.options.${optionValue}`;
};

/**
 * Helper function to create translation key for an attribute schema option's label
 */
export const getAttributeSchemaOptionLabelKey = (keyPrefix: string): string => {
    return `${keyPrefix}.label`;
};

/**
 * Helper function to create the keyPrefix for a metrics definition
 */
export const getMetricsDefinitionKeyPrefix = (keyPrefix: string, metricsDefinitionId: string): string => {
    return `${keyPrefix}.metrics_definitions.${metricsDefinitionId}`;
};

/**
 * Helper function to create translation key for a metrics definition display name
 */
export const getMetricsDefinitionDisplayNameKey = (keyPrefix: string): string => {
    return `${keyPrefix}.display_name`;
};


/**
 * Helper function to create the keyPrefix for a numeric metrics definition
 */
export const getNumericMetricsDefinitionKeyPrefix = (keyPrefix: string, metricName: string): string => {
    return `${keyPrefix}.numeric_metrics.${metricName}`;
};

/**
 * Helper function to create translation key for a numeric metric display name
 */
export const getNumericMetricDisplayNameKey = (keyPrefix: string): string => {
    return `${keyPrefix}.display_name`;
};

/**
 * Helper function to create translation key for a numeric metric unit
 */
export const getNumericMetricUnitKey = (keyPrefix: string): string => {
    return `${keyPrefix}.unit`;
};

/**
 * Helper function to create translation key for a numeric metric evaluator display name
 */
export const getNumericMetricEvaluatorDisplayNameKey = (keyPrefix: string): string => {
    return `${keyPrefix}.evaluator.display_name`;
};

/**
 * Helper function to create the keyPrefix for a numeric metrics definition
 */
export const getTagMetricsDefinitionKeyPrefix = (keyPrefix: string, metricName: string): string => {
    return `${keyPrefix}.tag_metrics.${metricName}`;
};

/**
 * Helper function to create translation key for a tag metric display name
 */
export const getTagMetricDisplayNameKey = (keyPrefix: string): string => {
    return `${keyPrefix}.display_name`;
};

/**
 * Helper function to create the keyPrefix for a numeric metrics definition
 */
export const getEnumDefinitionKeyPrefix = (keyPrefix: string, enumValue: string): string => {
    return `${keyPrefix}.enum_definitions.${enumValue}`;
};

/**
 * Helper function to create translation key for an enum definition name
 */
export const getEnumDefinitionNameKey = (keyPrefix: string): string => {
    return `${keyPrefix}.name`;
};

export const replaceEntityClassAttributeSchemaAttributeValuesWithTranslationKeys = (
    keyPrefix: string,
    attributeSchema: EntityClass['attributeSchema'],
): EntityClass['attributeSchema'] => {
    if (attributeSchema) {
        return attributeSchema.map((schema) => {
            const attrKeyPrefix = getAttributeSchemaKeyPrefix(keyPrefix, schema.key);

            return {
                ...schema,
                label      : getAttributeSchemaLabelKey(attrKeyPrefix),
                description: getAttributeSchemaDescriptionKey(attrKeyPrefix),
                options    : schema.options ? schema.options.map((option) => {
                    const optionKeyPrefix = getAttributeSchemaOptionKeyPrefix(attrKeyPrefix, option.value.toString());
                    return {
                        ...option,
                        label: getAttributeSchemaOptionLabelKey(optionKeyPrefix),
                    };
                }) : undefined,
            };
        });
    }
    return undefined;
};

export const replaceEntityClassMetricsDefinitionsAttributeValuesWithTranslationKeys = (
    keyPrefix: string,
    metricsDefinitions: EntityClass['metricsDefinitions'],
): EntityClass['metricsDefinitions'] => {
    if (metricsDefinitions) {
        return metricsDefinitions.map((metricsDefinition) => {
            const metricsDefKeyPrefix = getMetricsDefinitionKeyPrefix(keyPrefix, metricsDefinition.metricsDefinitionId);

            return {
                ...metricsDefinition,
                displayName   : getMetricsDefinitionDisplayNameKey(metricsDefKeyPrefix),
                numericMetrics: metricsDefinition.numericMetrics?.map((metric) => {
                    const metricKeyPrefix = getNumericMetricsDefinitionKeyPrefix(metricsDefKeyPrefix, metric.name);
                    return {
                        ...metric,
                        displayName: getNumericMetricDisplayNameKey(metricKeyPrefix),
                        unit       : metric.unit ? getNumericMetricUnitKey(metricKeyPrefix) : undefined,
                        evaluator  : metric.evaluator ? {
                            ...metric.evaluator,
                            displayName: getNumericMetricEvaluatorDisplayNameKey(metricKeyPrefix),
                        } : undefined,
                    };
                }),
                tagMetrics: metricsDefinition.tagMetrics?.map((metric) => {
                    const metricKeyPrefix = getTagMetricsDefinitionKeyPrefix(metricsDefKeyPrefix, metric.name);
                    return {
                        ...metric,
                        displayName    : getTagMetricDisplayNameKey(metricKeyPrefix),
                        enumDefinitions: metric.enumDefinitions ? metric.enumDefinitions.map((enumDef) => {
                            const enumKeyPrefix = getEnumDefinitionKeyPrefix(metricKeyPrefix, enumDef.value);
                            return {
                                ...enumDef,
                                name: getEnumDefinitionNameKey(enumKeyPrefix),
                            };
                        }) : undefined,
                    };
                }),
            };
        });
    }
    return undefined;
};

export const replaceEntityClassDisplaySettingsWithTranslationKeys = (
    keyPrefix: string,
    displaySettings: EntityClass['displaySettings'],
): EntityClass['displaySettings'] => {
    return {
        ...displaySettings,
        name       : getEntityClassNameKey(keyPrefix),
        description: getEntityClassDescriptionKey(keyPrefix),
    };
};

export const localizeEntityClass = (entityClass: EntityClass): Partial<EntityClass> => {
    const keyPrefix = getEntityClassKeyPrefix(entityClass.entityClass);

    const localizedEntityClass: any = {};
    Object.keys(entityClass).forEach((key) => {
        if (key === 'entityClass') {
            localizedEntityClass.entityClass = entityClass.entityClass;
        }
        if (key === 'blobDefinitions') {
            localizedEntityClass.blobDefinitions = entityClass.blobDefinitions;
        }
        if (key === 'displaySettings') {
            localizedEntityClass.displaySettings = replaceEntityClassDisplaySettingsWithTranslationKeys(keyPrefix, entityClass.displaySettings);
        }
        if (key === 'attributeSchema') {
            localizedEntityClass.attributeSchema = replaceEntityClassAttributeSchemaAttributeValuesWithTranslationKeys(
                keyPrefix, entityClass.attributeSchema,
            );
        }
        if (key === 'metricsDefinitions') {
            localizedEntityClass.metricsDefinitions = replaceEntityClassMetricsDefinitionsAttributeValuesWithTranslationKeys(
                keyPrefix, entityClass.metricsDefinitions,
            );
        }
    });
    return localizedEntityClass;
};
